import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

Chart.register(...registerables);

interface JobWagesBySectorComparisonProps {
  location: string;
}

// Main sectors from Census Bureau
const MAIN_SECTORS = [
  'Management, business, science, and arts occupations',
  'Service occupations',
  'Sales and office occupations',
  'Natural resources, construction, and maintenance occupations',
  'Production, transportation, and material moving occupations'
];

const SECTOR_SHORT_NAMES: Record<string, string> = {
  'Management, business, science, and arts occupations': 'Professional & Technical',
  'Service occupations': 'Service',
  'Sales and office occupations': 'Sales & Office',
  'Natural resources, construction, and maintenance occupations': 'Construction & Maintenance',
  'Production, transportation, and material moving occupations': 'Production & Transportation'
};

// Comparison chart: local vs California averages
const JobWagesBySectorComparison: React.FC<JobWagesBySectorComparisonProps> = ({ location }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);
  const [hasData, setHasData] = React.useState<boolean | null>(null);

  const buildChart = useCallback(async () => {
    if (!canvasRef.current) return;
    if (creatingRef.current) return;
    creatingRef.current = true;

    try {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch {}

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    await supabaseLocationService.initialize();
    const geofips = await supabaseLocationService.getGeofips(location);
    if (!geofips) {
      console.warn('JobWagesBySectorComparison: No geofips found for location:', location);
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    // Find latest year - use geofips (numeric) FIRST - this is the primary column
    let { data: years, error: yearsError } = await supabaseClient
      .from('job_by_wage_level')
      .select('year, geofips, location_name')
      .eq('geofips', geofips)
      .order('year', { ascending: false })
      .limit(1);

    // If no data by geofips, try geoid (text) as fallback
    if ((!years || years.length === 0) && !yearsError) {
      const geoid = String(geofips);
      const geoidResult = await supabaseClient
        .from('job_by_wage_level')
        .select('year, geofips, location_name')
        .eq('geoid', geoid)
        .order('year', { ascending: false })
        .limit(1);
      
      if (geoidResult.data && geoidResult.data.length > 0) {
        years = geoidResult.data;
        yearsError = geoidResult.error;
      }
    }

    // If still no data, try location_name
    if ((!years || years.length === 0) && !yearsError) {
      const nameResult = await supabaseClient
        .from('job_by_wage_level')
        .select('year, geofips, location_name')
        .ilike('location_name', `%${location}%`)
        .order('year', { ascending: false })
        .limit(1);
      
      if (nameResult.data && nameResult.data.length > 0) {
        years = nameResult.data;
        yearsError = nameResult.error;
        console.log('JobWagesBySectorComparison: Found data by location_name, geofips:', nameResult.data[0].geofips);
      }
    }

    if (yearsError) {
      console.warn('JobWagesBySectorComparison: Error fetching years:', yearsError);
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    const latestYear = years && years.length > 0 ? Number(years[0].year) : null;
    if (!latestYear) {
      console.warn('JobWagesBySectorComparison: No years found for', location, 'geofips:', geofips);
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    // Use the geofips we found (might be from location_name lookup)
    const actualGeofips = years && years[0].geofips ? Number(years[0].geofips) : geofips;

    // Fetch local and California data in parallel - use geofips (numeric) FIRST
    let [localResult, caResult] = await Promise.all([
      supabaseClient
        .from('job_by_wage_level')
        .select('job_type, median_income, gender')
        .eq('geofips', actualGeofips)
        .eq('year', latestYear),
      supabaseClient
        .from('job_by_wage_level')
        .select('job_type, median_income, gender')
        .eq('geofips', 6) // California geofips
        .eq('year', latestYear)
    ]);

    // If no local data by geofips, try geoid (text) as fallback
    if ((!localResult.data || localResult.data.length === 0) && !localResult.error) {
      const geoid = String(actualGeofips);
      const geoidResult = await supabaseClient
        .from('job_by_wage_level')
        .select('job_type, median_income, gender')
        .eq('geoid', geoid)
        .eq('year', latestYear);
      
      if (geoidResult.data && geoidResult.data.length > 0) {
        localResult = geoidResult;
      }
    }

    // If no CA data by geofips, try geoid (text) as fallback
    if ((!caResult.data || caResult.data.length === 0) && !caResult.error) {
      const caGeoidResult = await supabaseClient
        .from('job_by_wage_level')
        .select('job_type, median_income, gender')
        .eq('geoid', '6') // California geoid (text)
        .eq('year', latestYear);
      
      if (caGeoidResult.data && caGeoidResult.data.length > 0) {
        caResult = caGeoidResult;
      }
    }

    if (localResult.error || caResult.error) {
      console.warn('JobWagesBySectorComparison: Error fetching data:', localResult.error || caResult.error);
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    const allLocalData = localResult.data || [];
    const allCaData = caResult.data || [];

    if (allLocalData.length === 0 || allCaData.length === 0) {
      console.warn('JobWagesBySectorComparison: No data found. Local:', allLocalData.length, 'CA:', allCaData.length);
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    // Filter to main sectors and gender='both' (or any if 'both' doesn't exist)
    const hasBothGenderLocal = allLocalData.some((r: any) => (r.gender || '').toLowerCase() === 'both');
    const hasBothGenderCA = allCaData.some((r: any) => (r.gender || '').toLowerCase() === 'both');
    
    const localData = allLocalData.filter((r: any) => {
      const jobType = (r.job_type || '').trim();
      const gender = (r.gender || '').trim();
      const isMainSector = MAIN_SECTORS.some(sector => sector.trim() === jobType);
      // If 'both' exists in data, only use 'both'. Otherwise, use any gender.
      const genderMatch = hasBothGenderLocal ? gender.toLowerCase() === 'both' : true;
      return isMainSector && genderMatch;
    });

    const caData = allCaData.filter((r: any) => {
      const jobType = (r.job_type || '').trim();
      const gender = (r.gender || '').trim();
      const isMainSector = MAIN_SECTORS.some(sector => sector.trim() === jobType);
      // If 'both' exists in data, only use 'both'. Otherwise, use any gender.
      const genderMatch = hasBothGenderCA ? gender.toLowerCase() === 'both' : true;
      return isMainSector && genderMatch;
    });

    if (localData.length === 0 || caData.length === 0) {
      console.warn('JobWagesBySectorComparison: No matching sectors. Local sectors:', localData.length, 'CA sectors:', caData.length);
      setHasData(false);
      creatingRef.current = false;
      return;
    }

    setHasData(true);

    // Create maps for easy lookup
    const localMap = new Map(localData.map((r: any) => [r.job_type, Number(r.median_income || 0)]));
    const caMap = new Map(caData.map((r: any) => [r.job_type, Number(r.median_income || 0)]));

    // Order sectors by local wage (highest to lowest)
    const sectors = Array.from(localMap.keys()).sort((a, b) => {
      return (localMap.get(b) || 0) - (localMap.get(a) || 0);
    });

    const sectorLabels = sectors.map(s => SECTOR_SHORT_NAMES[s] || s);
    const localWages = sectors.map(s => localMap.get(s) || 0);
    const caWages = sectors.map(s => caMap.get(s) || 0);

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: sectorLabels,
        datasets: [
          {
            label: location,
            data: localWages,
            backgroundColor: '#3b82f6', // Blue
            borderColor: '#2563eb',
            borderWidth: 1,
          },
          {
            label: 'California',
            data: caWages,
            backgroundColor: '#9ca3af', // Gray
            borderColor: '#6b7280',
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(`Median Income by Sector - ${location} vs California`),
          tooltip: {
            callbacks: {
              label: (ctx: any) => `${ctx.dataset.label}: $${(ctx.parsed.y || 0).toLocaleString()}`,
            }
          }
        },
        scales: {
          y: {
            title: { display: true, text: 'Median Annual Income (USD)' },
            beginAtZero: true,
          },
          x: {
            title: { display: true, text: 'Economic Sector' },
            ticks: { autoSkip: false, maxRotation: 45, minRotation: 0 },
          },
        },
        interaction: { mode: 'index', intersect: false },
      },
    });

    creatingRef.current = false;
  }, [location]);

  useEffect(() => {
    buildChart();
    const canvasNode = canvasRef.current;
    return () => {
      try {
        if (chartRef.current) chartRef.current.destroy();
        if (canvasNode) {
          const existing = Chart.getChart(canvasNode as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch {}
    };
  }, [buildChart]);

  if (hasData === false) {
    return (
      <div className="chart-container" style={{ padding: '40px', textAlign: 'center' }}>
        <p>No job wages comparison data available for {location}.</p>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <canvas ref={canvasRef} style={{ height: '400px' }} />
    </div>
  );
};

export default JobWagesBySectorComparison;

